import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import BotonAcceso from '../elementos/BotonAcceso';
import { useTheme } from '../guardarToken/themeContext';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const apiUrlPlatos = process.env.REACT_APP_API_PLATOS || '/api/menus/platos/';
// Determina base API: si es absoluta, respétala; si es relativa usa el mismo origen del frontend (producción)
const apiBaseUrl = apiUrlPlatos.startsWith('http')
  ? apiUrlPlatos.replace(/\/platos\/?$/, '')
  : `${window.location.origin}${apiUrlPlatos.replace(/\/platos\/?$/, '')}`;

const EditPlatos = () => {
  const { id_plato } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [tipo, setTipo] = useState('');
  const [sedesSeleccionadas, setSedesSeleccionadas] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [tiposMenu, setTiposMenu] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Cargar datos en paralelo para mayor eficiencia
        const [resPlato, resSedes, resTipos] = await Promise.all([
          axios.get(`${apiUrlPlatos}${id_plato}`),
          axios.get(`${apiBaseUrl}/sedes`),
          axios.get(`${apiBaseUrl}/tipos-menu`)
        ]);

        const data = resPlato.data;
        if (data) {
          setNombre(data.nombre || '');
          setDescripcion(data.descripcion || '');
          setTipo(data.tipo || '');

          if (data.sedes && Array.isArray(data.sedes)) {
            setSedesSeleccionadas(data.sedes.map(s => s.id));
          }
        }

        setSedes(resSedes.data.filter(s => s.activo));
        setTiposMenu(resTipos.data);

      } catch (error) {
        console.error('Error al obtener datos:', error);
        setSnackbarMessage('Error al cargar la información del plato');
        setSnackbarSeverity('error');
        setShowSnackbar(true);
      } finally {
        setLoading(false);
      }
    };

    if (id_plato) {
      fetchData();
    }
  }, [id_plato]);

  const handleSedeToggle = (sedeId) => {
    setSedesSeleccionadas(prev =>
      prev.includes(sedeId)
        ? prev.filter(id => id !== sedeId)
        : [...prev, sedeId]
    );
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    try {
      await axios.put(`${apiUrlPlatos}${id_plato}`, {
        nombre: nombre || null,
        descripcion: descripcion || null,
        tipo: tipo || null,
        sede_ids: setSedesSeleccionadas
          ? sedesSeleccionadas
          : []
      });

      setSnackbarMessage('Plato actualizado correctamente');
      setSnackbarSeverity('success');
      setShowSnackbar(true);

      setTimeout(() => {
        navigate('/admin/AbmPlatos');
      }, 1500);
    } catch (error) {
      console.error('Error al actualizar el plato:', error);
      setSnackbarMessage('Ocurrió un error al actualizar');
      setSnackbarSeverity('error');
      setShowSnackbar(true);
    }
  };

  if (loading) {
    return (
      <Box className="flex justify-center items-center h-screen" sx={{ bgcolor: isDarkMode ? '#0f172a' : '#f8fafc' }}>
        <CircularProgress color="warning" />
      </Box>
    );
  }

  const labelStyle = {
    display: 'block',
    mb: 1,
    fontSize: '0.7rem',
    fontWeight: '800',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: isDarkMode ? '#f97316' : '#ea580c',
    ml: 1
  };

  return (
    <Box className={`min-h-screen px-4 py-8 md:py-12 transition-colors duration-300 ${isDarkMode ? 'bg-slate-950' : 'bg-slate-50'}`}>
      <div className="max-w-3xl mx-auto space-y-6">

        {/* HEADER */}
        <div className={`flex items-center gap-4 mb-2 animate-in slide-in-from-top duration-700`}>
          <button
            onClick={() => navigate('/admin/AbmPlatos')}
            className={`p-2.5 rounded-xl transition-all ${isDarkMode ? 'bg-slate-900 border-slate-800 text-slate-400 hover:text-white' : 'bg-white border-gray-200 text-gray-500 hover:text-gray-800'} border shadow-sm group`}
          >
            <ArrowBackIcon fontSize="small" className="transition-transform group-hover:-translate-x-1" />
          </button>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></div>
              <h1 className={`text-2xl font-black tracking-tight ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                Editar Plato
              </h1>
            </div>
            <p className={`text-[11px] font-black uppercase tracking-widest ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              ID: {id_plato} • Configuración de oferta gastronómica
            </p>
          </div>
        </div>

        <Paper
          elevation={0}
          className={`p-8 md:p-10 rounded-3xl border transition-all duration-300 animate-in slide-in-from-bottom duration-1000 ${isDarkMode
            ? 'bg-slate-900 border-slate-800 shadow-2xl shadow-black/20'
            : 'bg-white border-slate-100 shadow-xl shadow-slate-200/50'
            }`}
        >
          <div className="flex items-center gap-2 mb-8 text-orange-500">
            <SaveIcon fontSize="small" />
            <span className="text-[10px] font-black uppercase tracking-[0.2em]">Formulario de Edición</span>
          </div>
          <form onSubmit={handleUpdate} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* NOMBRE */}
              <Box>
                <Typography variant="caption" sx={labelStyle}>Nombre del Plato</Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  required
                  placeholder="Ej: Pechuga con arroz"
                  InputProps={{
                    style: {
                      color: isDarkMode ? 'white' : '#1e293b',
                      borderRadius: '12px'
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: isDarkMode ? '#0f172a' : '#f8fafc',
                      '& fieldset': { borderColor: isDarkMode ? '#334155' : '#e2e8f0' },
                      '&:hover fieldset': { borderColor: '#f97316' },
                      '&.Mui-focused fieldset': { borderColor: '#f97316' },
                    }
                  }}
                />
              </Box>

              {/* TIPO */}
              <Box>
                <Typography variant="caption" sx={labelStyle}>Tipo de Menú</Typography>
                <FormControl fullWidth>
                  <Select
                    value={tipo}
                    onChange={(e) => setTipo(e.target.value)}
                    required
                    displayEmpty
                    sx={{
                      color: isDarkMode ? 'white' : '#1e293b',
                      bgcolor: isDarkMode ? '#0f172a' : '#f8fafc',
                      borderRadius: '12px',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: isDarkMode ? '#334155' : '#e2e8f0' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#f97316' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#f97316' },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: isDarkMode ? '#1e293b' : 'white',
                          color: isDarkMode ? 'white' : '#1e293b',
                          borderRadius: '12px',
                          mt: 1,
                          boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                        }
                      }
                    }}
                  >
                    <MenuItem value="" disabled>Seleccionar tipo</MenuItem>
                    {tiposMenu.map((t) => (
                      <MenuItem key={t.id} value={t.nombre}>{t.nombre}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* DESCRIPCIÓN */}
              <Box className="md:col-span-2">
                <Typography variant="caption" sx={labelStyle}>Descripción</Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={3}
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  placeholder="Detalles sobre el plato..."
                  InputProps={{
                    style: {
                      color: isDarkMode ? 'white' : '#1e293b',
                      borderRadius: '12px'
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: isDarkMode ? '#0f172a' : '#f8fafc',
                      '& fieldset': { borderColor: isDarkMode ? '#334155' : '#e2e8f0' },
                      '&:hover fieldset': { borderColor: '#f97316' },
                      '&.Mui-focused fieldset': { borderColor: '#f97316' },
                    }
                  }}
                />
              </Box>
            </div>

            <Box>
              <Typography variant="caption" sx={{ ...labelStyle, color: isDarkMode ? '#60a5fa' : '#2563eb' }}>
                Asignación de Sedes
              </Typography>
              <div className={`grid grid-cols-2 sm:grid-cols-3 gap-3 p-5 rounded-xl border transition-colors ${isDarkMode ? 'bg-slate-950/50 border-slate-800' : 'bg-slate-50 border-slate-200'}`}>
                {sedes.map(sede => (
                  <FormControlLabel
                    key={sede.id}
                    control={
                      <Checkbox
                        checked={sedesSeleccionadas.includes(sede.id)}
                        onChange={() => handleSedeToggle(sede.id)}
                        sx={{
                          color: isDarkMode ? '#475569' : '#cbd5e1',
                          '&.Mui-checked': { color: '#f97316' }
                        }}
                      />
                    }
                    label={<span className={`text-[11px] font-bold ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{sede.nombre}</span>}
                  />
                ))}
              </div>
            </Box>

            <div className="flex justify-end gap-3 pt-6 border-t border-slate-700/20">
              <button
                type="button"
                onClick={() => navigate('/admin/AbmPlatos')}
                className={`px-6 py-2.5 rounded-xl border transition-all font-bold text-[10px] uppercase tracking-wider ${isDarkMode ? 'border-slate-700 text-slate-400 hover:bg-slate-800' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
              >
                Cancelar
              </button>
              <BotonAcceso
                variant="accent"
                type="submit"
                className="px-8 py-2.5 rounded-xl bg-orange-600 hover:bg-orange-700 text-white shadow-lg shadow-orange-950/20 transition-all font-bold text-[10px] uppercase tracking-wider flex items-center gap-2"
              >
                <SaveIcon sx={{ fontSize: 16 }} /> Guardar Cambios
              </BotonAcceso>
            </div>
          </form>
        </Paper>
      </div>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowSnackbar(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '12px', fontWeight: 'bold' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditPlatos;

